/**
 * User Invite Modal Component
 *
 * Modal for inviting new users to the tenant.
 *
 * @fileoverview Invite user modal component
 */

import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  VStack,
  useColorModeValue,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import type { InviteTenantUserInput, TenantRole } from '../../types/users';

interface UserInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteTenantUserInput) => Promise<void>;
  loading: boolean;
  error: string | null;
}

interface FormErrors {
  email?: string;
}

/**
 * User Invite Modal Component
 *
 * Modal dialog for inviting new users to the tenant.
 */
export const UserInviteModal: React.FC<UserInviteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
}) => {
  const bgColor = useColorModeValue('white', 'xacto.deepNavy');

  const [formData, setFormData] = useState({
    email: '',
    tenantRole: 'USER' as TenantRole,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Must be a valid email address';
        }
        break;
    }
    return undefined;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const fieldError = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldError = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: fieldError }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    const emailError = validateField('email', formData.email);
    if (emailError) {
      newErrors.email = emailError;
      isValid = false;
    }

    setErrors(newErrors);
    setTouched({ email: true });
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    await onSubmit({
      email: formData.email.trim().toLowerCase(),
      tenantRole: formData.tenantRole,
    });

    // Reset form on success
    setFormData({ email: '', tenantRole: 'USER' });
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    setFormData({ email: '', tenantRole: 'USER' });
    setErrors({});
    setTouched({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Invite User</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4}>
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <FormControl isRequired isInvalid={touched.email && !!errors.email}>
                <FormLabel>Email Address</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="user@example.com"
                />
                <FormHelperText>
                  An invitation email will be sent to this address
                </FormHelperText>
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  name="tenantRole"
                  value={formData.tenantRole}
                  onChange={handleChange}
                >
                  <option value="USER">Member</option>
                  <option value="TENANT_ADMIN">Admin</option>
                </Select>
                <FormHelperText>
                  Admins can manage tenant users and settings
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText="Sending..."
            >
              Send Invite
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UserInviteModal;
